<!-- BaseTinyEditor.vue (common) -->
<template tag="공통 에디터 컴포넌트">
  <div>
    <Editor
      ref="editorRef"
      v-if="isEditorReady"
      :key="`admin-${editorKey}`"
      v-model="model"
      :init="adminInitOptions"
      :readonly="readonly"
      :tinymceScriptSrc="tinymceScript"
    />
    <!-- 로딩 상태 -->
    <div v-else class="editor-loading">loading...</div>

    <!-- Base.Popup를 활용한 미리보기 -->
    <Base.Popup v-model="showPreview">
      <template #title>📄{{ $text("preview") }}</template>

      <div v-dompurify-html="sanitizedContent"></div>

      <template #footer="{ close }">
        <button class="btn btn_default btn_md" @click="close" type="button">
          {{ $text("button.close") }}
        </button>
      </template>
    </Base.Popup>
  </div>
</template>

<script setup lang="ts">
import { global, i18n } from "@/app/plugins/global";

import { ref, onMounted, onBeforeUnmount, nextTick, onActivated, onDeactivated, watch } from "vue";
import Editor from "@tinymce/tinymce-vue";
import { type Editor as EditorType } from "tinymce";

import { Base } from "../BaseFactory";
import { getBase64ToFile } from "@/api/cmmn/FileApi";

// dynamically resolve base path
const basePath = import.meta.env.BASE_URL;
const tinymceScript = basePath + "tinymce/tinymce.min.js";

/* ------------------------------------------------------------
                          Vue Instance & Global Properties
   ------------------------------------------------------------ */
const { $text } = i18n();
const vm = global();

/* ------------------------------------------------------------
                          Model
   ------------------------------------------------------------ */
/** admin 모드용  */
const model = defineModel<string>();

/* ------------------------------------------------------------
                          Props
   ------------------------------------------------------------ */
const props = defineProps({
  id: { type: String, default: "" },
  readonly: { type: Boolean, default: false }, // admin 모드용
  disabled: { type: Boolean, default: false }, // 일반 모드용
  maxLength: { type: Number, default: 3000 },
  enableLimit: { type: Boolean, default: true },
  height: { type: Number, default: 700 },
  // 에디터에서 이미지 업로드 시 파일이 저장될 테이블 이름
  // 지정하지 않으면 "files"로 업로드됨
  imageTableName: { type: String, default: "files" },
});

/* ------------------------------------------------------------
                          Variable Declarations
   ------------------------------------------------------------ */
const editorRef = ref();
const editorInstance = ref<EditorType | null>(null);
const textLength = ref(0);
const isEditorReady = ref(false);
const editorKey = ref(Date.now()); // 고유 키 생성
const storedLang = sessionStorage.getItem("lang");
const imageMimeTypes = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/bmp",
  "image/webp",
];

// CSP 안전한 미리보기 모달 상태
const showPreview = ref(false);
const sanitizedContent = ref("");

const readAsDataUrl = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("file read failed"));
    reader.readAsDataURL(blob);
  });
};

const uploadBase64Image = async (base64: string, context: string): Promise<string | null> => {
  try {
    const filePath = await getBase64ToFile(base64, props.imageTableName);
    if (!filePath) {
      vm.$toast($text("editor.imageUploadFail"), "error");
      return null;
    }
    return filePath;
  } catch (error) {
    console.error(`이미지 업로드 중 오류(${context}):`, error);
    vm.$toast($text("editor.imageUploadFail"), "error");
    return null;
  }
};

// Admin 모드 TinyMCE 옵션
const adminInitOptions = {
  base_url: basePath + "tinymce", // self-hosted base
  height: props.height,
  menubar: "format insert tools table",
  statusbar: false,
  branding: false,
  promotion: false,
  plugins: "lists link code table media",
  toolbar:
    "undo redo | formatselect | bold italic | forecolor backcolor |" +
    "alignleft aligncenter alignright alignjustify | " +
    "bullist numlist outdent indent | customimage link table | removeformat | code media",
  elementpath: false,
  paste_data_images: true,
  paste_webkit_styles: "all",
  paste_merge_formats: true,
  automatic_uploads: true,
  relative_urls: false,
  images_upload_handler: (blobInfo: { blob: () => Blob }) => {
    return new Promise((resolve, reject) => {
      readAsDataUrl(blobInfo.blob())
        .then((base64) => uploadBase64Image(base64, "auto"))
        .then((filePath) => {
          if (!filePath) {
            reject(new Error("image upload failed"));
            return;
          }
          resolve(filePath);
        })
        .catch((error) => reject(error));
    });
  },
  valid_elements: "*[*]",
  content_css: basePath + "tinymce/tiny-editor-content.css",
  language: sessionStorage.getItem("lang"),
  language_url: `/tinymce/lang/${storedLang}.js`,
  //the Tiny Community Language Packages download page: https://www.tiny.cloud/get-tiny/language-packages/
  setup: function (editor: EditorType) {
    // 에러 처리 추가
    editor.on("error", (e: unknown) => {
      console.error("TinyMCE 런타임 에러:", e);
    });

    editor.on("keydown", function (e: KeyboardEvent) {
      if (textLength.value >= props.maxLength && e.keyCode !== 8 && e.keyCode !== 46) {
        e.preventDefault();
        vm.$toast($text("editor.maxLength"), "error");
      }
    });

    editor.on("input keyup setcontent", function () {
      const textOnly = editor.getContent({ format: "text" }).trim();
      textLength.value = textOnly.length;
    });

    editor.on("paste", function (e: ClipboardEvent) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const clipboardData = e.clipboardData || (window as any).clipboardData;
      if (!clipboardData) return;

      // 일반 텍스트 붙여넣기 시 글자 수 제한
      const pastedText = clipboardData.getData("text/plain");
      const allowedLength = Math.max(props.maxLength - textLength.value, 0);

      if (pastedText.length > allowedLength) {
        e.preventDefault();
        if (allowedLength > 0) {
          const trimmedText = pastedText.slice(0, allowedLength);
          editor.insertContent(trimmedText);
        }
        vm.$toast($text("editor.maxLength"), "error");
      }
    });
    editorInstance.value = editor;

    editor.ui.registry.addButton("customimage", {
      icon: "image",
      tooltip: $text("editor.imageTooltip"),
      onAction: () => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/jpeg,image/jpg,image/png,image/gif,image/bmp,image/webp";

        input.onchange = (e: Event) => {
          const target = e.target as HTMLInputElement;
          const files = target.files;
          if (files && files.length > 0) {
            const file = files[0];
            // 파일 타입 검증
            if (!imageMimeTypes.includes(file.type)) {
              vm.$toast($text("editor.noImage"), "error");
              return;
            }

            readAsDataUrl(file)
              .then((base64) => uploadBase64Image(base64, "button"))
              .then((filePath) => {
                if (!filePath) return;
                editor.insertContent(`<img src="${filePath}" alt="${file.name}" />`);
              });
          }
        };

        input.click();
      },
    });
  },

  // media_url_resolver 옵션 추가
  media_url_resolver: () => {
    return new Promise((resolve) => {
      resolve({ html: "" });
    });
  },
};

/* ------------------------------------------------------------
                          watch
   ------------------------------------------------------------ */
/** 모달이 닫힐 때 content 초기화 */
watch(showPreview, (newVal) => {
  if (!newVal) {
    sanitizedContent.value = "";
  }
});

/* ------------------------------------------------------------
                                  Methods
   ------------------------------------------------------------ */
/** 에디터 정리 함수 */
const cleanupEditor = () => {
  isEditorReady.value = false;

  if (editorInstance.value) {
    try {
      if (!editorInstance.value.destroyed) {
        editorInstance.value.destroy();
      }
    } catch (error) {
      console.warn("에디터 정리 중 경고:", error);
    } finally {
      editorInstance.value = null;
    }
  }

  // TinyMCE 전역 인스턴스 정리
  try {
    if (window.$tinymce && window.$tinymce.editors) {
      const editors = window.$tinymce.editors.slice(); // 복사본 생성
      editors.forEach((editor: EditorType) => {
        try {
          if (editor && !editor.destroyed) {
            editor.destroy();
          }
        } catch (e) {
          console.warn("전역 에디터 정리 중 경고:", e);
        }
      });
    }
  } catch (error) {
    console.warn("TinyMCE 전역 정리 중 경고:", error);
  }
};

/** Base.Popup를 활용한 미리보기 실행 */
const executePreview = () => {
  const editor = editorInstance.value || editorRef.value?.editor;

  if (!editor) {
    vm.$toast("에디터가 로드되지 않았습니다.", "error");
    return;
  }

  try {
    const content = editor.getContent();

    if (!content || content.trim() === "") {
      vm.$toast("미리보기할 내용이 없습니다.", "warning");
      return;
    }

    // HTML sanitization 적용
    sanitizedContent.value = content; // 더 이상 정제 로직 제거

    // 모달 표시
    showPreview.value = true;
  } catch (error) {
    console.error("미리보기 실행 중 오류:", error);
    vm.$toast("미리보기를 실행할 수 없습니다.", "error");
  }
};

/* ------------------------------------------------------------
                          Lifecycle
   ------------------------------------------------------------ */
/** 페이지 렌더링 시 초기값 설정 */
onMounted(async () => {
  // DOM 안정화 후 에디터 활성화
  await nextTick();
  setTimeout(() => {
    isEditorReady.value = true;
  }, 100);
});

/** 컴포넌트 언마운트 시 정리 */
onBeforeUnmount(() => {
  cleanupEditor();
});

/** keep-alive 사용 시 처리 */
onDeactivated(() => {
  cleanupEditor();
});

/** keep-alive 사용 시 처리 */
onActivated(async () => {
  await nextTick();
  setTimeout(() => {
    editorKey.value = Date.now(); // 새로운 키 생성
    isEditorReady.value = true;
  }, 100);
});

/* ------------------------------------------------------------
                          Expose
   ------------------------------------------------------------ */
/** 외부에서 사용할 수 있도록 노출 */
defineExpose({
  executePreview,
  getEditor: () => editorRef.value?.editor,
});
</script>
